import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { decryptMessage } from "../Components/EncryptDecryptMessage/EncryptDecryptMessage";
import { playNotificationSound } from "../utils/notificationSound";

// Debug mode - controlled via env, but ALWAYS disabled in production builds.
// Set REACT_APP_NOTIFICATION_DEBUG=true in your local .env to inspect events locally.
// In production (NODE_ENV === "production") this flag is forcibly turned off
// to prevent excessive logging and performance degradation.
const DEBUG_MODE =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_NOTIFICATION_DEBUG === "true";

/**
 * Custom hook for handling message notifications
 * - Shows browser notifications when user is on different tab
 * - Shows glass toast notifications when user is viewing the app
 * - Plays sound for in-app notifications
 */
export const useNotificationSystem = (userInfo, activeChat, getSecretKey, chatConversationId, allConversations = []) => {
  const { socket, isConnected } = useSelector((state) => state.socket);
  const currentUserId = localStorage.getItem("userId");
  const notificationPermissionRef = useRef(null);
  const originalTitleRef = useRef(document.title);
  const unreadCountRef = useRef(0);
  const listenersRegisteredRef = useRef(false);
  const joinAppEmittedRef = useRef(false);
  const wildcardListenerRegisteredRef = useRef(false);
  const wildcardHandlerRef = useRef(null);
  const joinedConversationsRef = useRef(new Set()); // Track which conversations we've joined
  const allConversationsJoinedRef = useRef(false); // Track if we've joined all conversations
  
  // Use refs to store latest values without causing re-renders
  const activeChatRef = useRef(activeChat);
  const getSecretKeyRef = useRef(getSecretKey);
  const chatConversationIdRef = useRef(chatConversationId);
  
  // Update refs when values change
  useEffect(() => {
    activeChatRef.current = activeChat;
    getSecretKeyRef.current = getSecretKey;
    chatConversationIdRef.current = chatConversationId;
    
    // Join conversation room when chatConversationId changes
    if (socket && isConnected && chatConversationId) {
      const convId = typeof chatConversationId === "string" 
        ? chatConversationId 
        : chatConversationId?._id;
      
      if (convId && !joinedConversationsRef.current.has(convId)) {
        socket.emit("joinConversation", { id: convId });
        joinedConversationsRef.current.add(convId);
      }
    }
  }, [activeChat, getSecretKey, chatConversationId, socket, isConnected]);

  // Request notification permission on mount and keep it updated
  useEffect(() => {
    if ("Notification" in window) {
      // Always set the current permission state
      notificationPermissionRef.current = Notification.permission;
      
      if (Notification.permission === "default") {
        Notification.requestPermission().then((permission) => {
          notificationPermissionRef.current = permission;
          if (DEBUG_MODE) {
            console.log("🔔 Notification permission:", permission);
          }
        }).catch((err) => {
          // Even if request fails, set the current state
          notificationPermissionRef.current = Notification.permission;
          if (DEBUG_MODE) {
            console.warn("🔔 Notification permission request failed:", err);
          }
        });
      }
    } else {
      // Notification API not supported
      notificationPermissionRef.current = "unsupported";
    }
  }, []);

  // Setup socket connection and joinApp (runs once when socket connects)
  useEffect(() => {
    if (!socket || !isConnected) {
      if (DEBUG_MODE) {
        console.log("🔔 Socket not ready:", { socket: !!socket, isConnected });
      }
      // Reset flags when socket disconnects
      listenersRegisteredRef.current = false;
      joinAppEmittedRef.current = false;
      wildcardListenerRegisteredRef.current = false;
      wildcardHandlerRef.current = null;
      return;
    }

    // CRITICAL: Join the app to register this socket with the backend
    // This ensures the backend knows this user is online and can send events
    // Only emit once per connection
    if (currentUserId && !joinAppEmittedRef.current) {
      socket.emit("joinApp", { userId: currentUserId });
      joinAppEmittedRef.current = true;
    }
    
    // CRITICAL: Join ALL conversations the user is part of
    // This ensures we receive message-sent events for all conversations, not just the active one
    if (!allConversationsJoinedRef.current && allConversations && allConversations.length > 0) {
      allConversations.forEach((conv) => {
        const convId = typeof conv === "string" 
          ? conv 
          : conv?._id || conv?.conversationId?._id || conv?.conversationId;
        
        if (convId && !joinedConversationsRef.current.has(String(convId))) {
          socket.emit("joinConversation", { id: convId });
          joinedConversationsRef.current.add(String(convId));
        }
      });
      allConversationsJoinedRef.current = true;
    }
    
    // CRITICAL: Join the current conversation if we have one
    // This ensures we receive events for the active conversation
    if (chatConversationId) {
      const convId = typeof chatConversationId === "string" 
        ? chatConversationId 
        : chatConversationId?._id;
      
      if (convId && !joinedConversationsRef.current.has(String(convId))) {
        socket.emit("joinConversation", { id: convId });
        joinedConversationsRef.current.add(String(convId));
      }
    }

    // Listen to conversation-refresh to join new conversations
    const handleConversationRefresh = (data) => {
      const conversationId = data?.conversationId?._id || 
                             data?.conversationId || 
                             data?.id ||
                             data?.details?.conversationId?._id ||
                             data?.details?.conversationId;
      if (conversationId && !joinedConversationsRef.current.has(String(conversationId))) {
        socket.emit("joinConversation", { id: conversationId });
        joinedConversationsRef.current.add(String(conversationId));
      }
    };
    
    socket.on("conversation-refresh", handleConversationRefresh);

    // Cleanup on disconnect
    return () => {
      socket.off("conversation-refresh", handleConversationRefresh);
    };
  }, [socket, isConnected, currentUserId]);

  // Listen for new messages (separate effect to avoid re-registering on every prop change)
  useEffect(() => {
    if (!socket || !isConnected) {
      return;
    }

    // Prevent duplicate listener registration
    if (listenersRegisteredRef.current) {
      if (DEBUG_MODE) {
        console.log("🔔 Listeners already registered, skipping...");
      }
      return;
    }

    const handleReceiveMessage = async (data) => {
      try {
        const message = data.message || data; // Handle both formats
        
        // Validate message structure
        if (!message || !message._id) {
          if (DEBUG_MODE) {
            console.warn("🔔 Invalid message structure received:", data);
          }
          return;
        }
        
        // Get latest values from refs
        const currentActiveChat = activeChatRef.current;
        const currentGetSecretKey = getSecretKeyRef.current;
        const currentChatConversationId = chatConversationIdRef.current;
        
        if (DEBUG_MODE) {
          console.log("🔔 Socket Event Received:", {
            event: "message-sent or receive_message",
            messageId: message._id,
            conversationId: message.conversationId,
            senderId: message.senderId,
          });
        }
      
      // Don't notify for own messages
      // Be defensive: backend may send different shapes, so try multiple fields.
      let senderIdFromSender = null;
      if (message.senderId) {
        if (typeof message.senderId === "object") {
          senderIdFromSender =
            message.senderId._id ||
            message.senderId.id ||
            message.senderId.userId ||
            null;
        } else {
          senderIdFromSender = message.senderId;
        }
      }

      const possibleCurrentUserId =
        senderIdFromSender ||
        message.userId ||
        message.sender ||
        message.ownerId ||
        null;

      if (
        possibleCurrentUserId &&
        String(possibleCurrentUserId) === String(currentUserId)
      ) {
        if (DEBUG_MODE) {
          console.log("🚫 Notification blocked: Own message");
        }
        return;
      }

      // Don't notify if user is viewing this conversation AND tab is visible
      // Fix: Use loose comparison with string conversion to handle type mismatches
      const messageConversationId = message.conversationId?._id || message.conversationId || message.chatId;
      const currentConversationId = typeof currentChatConversationId === "string" 
        ? currentChatConversationId 
        : currentChatConversationId?._id;
      
      // Only block if viewing the EXACT same conversation AND tab is visible
      const isCurrentChat = messageConversationId && currentConversationId && 
        String(messageConversationId) === String(currentConversationId);
      
      // Only block if BOTH conditions are true:
      // 1. We're viewing the exact same conversation
      // 2. The tab is visible
      // If tab is hidden, always show notification even if viewing same conversation
      if (isCurrentChat && document.visibilityState === "visible") {
        if (DEBUG_MODE) {
          console.log("🚫 Notification blocked: User is viewing this chat");
        }
        return; // User is viewing this conversation, no notification needed
      }

      // Get sender info from message or activeChat
      let senderName = "Someone";
      let senderAvatar = "/user.png";

      // Try to get sender info from message
      if (message.senderId) {
        if (typeof message.senderId === 'object') {
          senderName = message.senderId.name || message.senderId.userName || senderName;
          senderAvatar = message.senderId.userImage || message.senderId.avtarImage || senderAvatar;
        }
      }

      // Fallback: try to get from activeChat (for 1:1 chats)
      if (currentActiveChat?.user) {
        senderName = currentActiveChat.user.name || currentActiveChat.user.userName || senderName;
        senderAvatar = currentActiveChat.user.userImage || currentActiveChat.user.avtarImage || senderAvatar;
      }

      // Decrypt message preview (if encrypted)
      let messagePreview = "[Encrypted message]";
      if (message.cipherText && message.iv && currentGetSecretKey) {
        try {
          const decrypted = await decryptMessage(
            message.cipherText,
            message.iv,
            currentGetSecretKey
          );
          messagePreview = decrypted.length > 50 
            ? decrypted.substring(0, 50) + "..." 
            : decrypted;
        } catch (err) {
          // Keep default encrypted message preview
        }
      } else if (message.message) {
        messagePreview = message.message.length > 50 
          ? message.message.substring(0, 50) + "..." 
          : message.message;
      }

      // Check if user is viewing the app
      const isVisible = document.visibilityState === "visible";

      if (DEBUG_MODE) {
        console.log("🔔 Processing notification:", {
          isVisible,
          notificationPermission: notificationPermissionRef.current,
          senderName,
          messagePreview: messagePreview.substring(0, 30) + "...",
        });
      }

      if (isVisible) {
        // User is viewing the app - show toast notification
        try {
          playNotificationSound();
        } catch (err) {
          // Silent failure for sound - not critical
        }

        // Custom toast with glass design
        try {
          toast(
            <div className="flex items-center gap-3 px-2 py-1">
              {/* Avatar */}
              <img
                src={senderAvatar}
                alt={senderName}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                onError={(e) => {
                  e.target.src = "/user.png";
                }}
              />
              {/* Message Content */}
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm truncate">
                  {senderName}
                </div>
                <div className="text-zinc-400 text-xs truncate">
                  {messagePreview}
                </div>
              </div>
            </div>,
            {
              position: "top-center",
              autoClose: 3000,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: true,
              draggable: true,
              className: "glass-toast",
            }
          );
        } catch (err) {
          console.error("🔔 Toast notification failed:", err);
        }
      } else {
        // User is on different tab - show browser notification
        // Always check current permission state (it might have changed)
        const currentPermission = Notification.permission;
        notificationPermissionRef.current = currentPermission;
        
        if (currentPermission === "granted") {
          try {
            // Best-effort: also play the notification sound even when tab is hidden.
            // Some browsers may block this due to autoplay policies, but it will
            // work where allowed.
            try {
              playNotificationSound();
            } catch (soundErr) {
              // Silent failure for sound
            }

            unreadCountRef.current += 1;
            
            // Update document title
            document.title = `(${unreadCountRef.current}) New Message from ${senderName}`;

            // Show browser notification
            const notification = new Notification(`${senderName}`, {
              body: messagePreview,
              icon: senderAvatar,
              badge: "/favicon.ico",
              tag: `message-${message._id}`, // Prevent duplicate notifications
              requireInteraction: false,
            });

            // Reset title when user comes back
            notification.onclick = () => {
              window.focus();
              document.title = originalTitleRef.current;
              unreadCountRef.current = 0;
              notification.close();
            };
          } catch (err) {
            console.error("🔔 Browser notification failed:", err);
          }
        }
      }
      } catch (error) {
        // Catch any errors in the notification handler
        console.error("🔔 Error in notification handler:", error);
      }
    };

    // Listen to the EXACT event name used by the backend
    // Based on backend code: io.to(`CONVERSATION__${conversationId}`).emit("message-sent", { message: msg })
    socket.on("message-sent", handleReceiveMessage);
    
    // Also listen to alternative event names in case backend uses different naming
    socket.on("receive_message", handleReceiveMessage);
    socket.on("new_message", handleReceiveMessage);

    listenersRegisteredRef.current = true;

    if (DEBUG_MODE) {
      console.log("🔔 Notification listeners registered for: message-sent, receive_message, new_message");
      console.log("🔔 Socket ID:", socket.id);
      console.log("🔔 Socket connected:", socket.connected);
      console.log("🔔 Notification permission:", notificationPermissionRef.current);
    }

    // Cleanup
    return () => {
      listenersRegisteredRef.current = false;
      socket.off("message-sent", handleReceiveMessage);
      socket.off("receive_message", handleReceiveMessage);
      socket.off("new_message", handleReceiveMessage);
    };
  }, [socket, isConnected, currentUserId]);

  // Reset title when user comes back to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        document.title = originalTitleRef.current;
        unreadCountRef.current = 0;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};


